import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, Table, Search, X, Calendar, PanelLeftClose, PanelLeftOpen, ChevronFirst, ChevronLeft, ChevronRight, ChevronLast } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ActionMenu from '../../components/common/ActionMenu';
import DownloadModal from '../../components/common/DownloadModal';
import { auditApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const DEFAULT_PAGE_SIZE = 25;

const COLUMNS = [
  { key: 'changedAt',        label: 'Date',        width: 188 },
  { key: 'changedBy',        label: 'User',        width: 200 },
  { key: 'tableName',        label: 'Table',       width: 120 },
  { key: 'recordPk',         label: 'ID',          width: 60  },
  { key: 'recordIdentifier', label: 'Identifier',  width: 140 },
  { key: 'action',           label: 'Action',      width: 80  },
  { key: 'fieldName',        label: 'Field',       width: 120 },
  { key: 'oldValue',         label: 'Old Value',   width: 360 },
  { key: 'newValue',         label: 'New Value',   width: 360 },
];

const ACTION_COLORS = {
  CREATE: { background: '#dcfce7', color: '#16a34a' },
  UPDATE: { background: '#dbeafe', color: '#2563eb' },
  DELETE: { background: '#fee2e2', color: '#dc2626' },
};

const AuditTrailCrudPage = () => {
  const { isSysAdmin }  = useAuth();
  const { notify }      = useNotifications();

  const [viewMode, setViewMode]           = useState('list');
  const [rows, setRows]                   = useState([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const [pageSize, setPageSize]           = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters]             = useState({});
  const [searchTerm, setSearchTerm]       = useState('');
  const [searchHovered, setSearchHovered] = useState(false);
  const [selectedRow, setSelectedRow]     = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);

  // Date filter popover
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom]             = useState('');
  const [dateTo, setDateTo]                 = useState('');
  const [appliedFrom, setAppliedFrom]       = useState('');
  const [appliedTo, setAppliedTo]           = useState('');
  const dateFilterRef                       = useRef(null);

  // Purge modal
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeDays, setPurgeDays]           = useState('');
  const [purging, setPurging]               = useState(false);
  const purgeDaysRef                        = useRef(null);

  // Download modal
  const [showDownload, setShowDownload] = useState(false);

  // Enter-triggered search (matches Dynamic Module pattern). The applied value
  // is what gets sent to the API; typing into the box does not fire requests.
  // Backend also enforces a 3-char minimum, so short terms are silently ignored
  // there — clearing the box below-3-char here as a client-side courtesy.
  const [appliedSearch, setAppliedSearch] = useState('');
  const submitSearch = useCallback((value) => {
    const trimmed = (value ?? '').trim();
    setAppliedSearch(trimmed.length >= 3 ? trimmed : '');
  }, []);

  const loadData = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, pageSize };
      if (appliedFrom) params.dateFrom = appliedFrom;
      if (appliedTo)   params.dateTo   = appliedTo;
      if (appliedSearch) params.search = appliedSearch;
      const res = await auditApi.getAllChanges(params);
      setRows(res.data.data);
      setTotalCount(res.data.totalCount);
      setPage(pg);
      setSelectedRow(null);
    } catch {
      notify.error('Failed to load change history.');
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo, appliedSearch, pageSize, notify]);

  useEffect(() => { loadData(1); }, [loadData]);

  useEffect(() => {
    const handler = e => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target))
        setShowDateFilter(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (showPurgeModal) setTimeout(() => purgeDaysRef.current?.focus(), 50);
  }, [showPurgeModal]);

  const applyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setShowDateFilter(false);
  };

  const clearDateFilter = () => {
    setDateFrom(''); setDateTo('');
    setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  };

  const handlePurge = async () => {
    const days = parseInt(purgeDays, 10);
    if (!days || days < 1) { notify.error('Enter a valid number of days.'); return; }
    if (!window.confirm(`Delete all audit records older than ${days} day(s)? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const res = await auditApi.purge(days);
      notify.success(`Purged ${res.data.deletedCount} record(s).`);
      setShowPurgeModal(false);
      setPurgeDays('');
      loadData(1);
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Purge failed.');
    } finally {
      setPurging(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const dateFilterActive = !!(appliedFrom || appliedTo);

  const gridData = rows.map(row => ({
    ...row,
    changedAt:        formatUtcDate(row.changedAt),
    recordIdentifier: row.recordIdentifier || '—',
    fieldName:        row.fieldName  || '—',
    oldValue:         row.oldValue   || '—',
    newValue:         row.newValue   || '—',
  }));

  // Search is now server-side (see debounced appliedSearch effect above).
  // Grid renders whatever the API returned for the current page.
  const visibleData = gridData;

  const showX = searchHovered && !!searchTerm;

  return (
    <div className="stui-module-container">

      {/* Header */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <div className="stui-module-header-tabs">
            <button
              className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            ><List size={18} /></button>
            <button
              className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`}
              onClick={() => setViewMode('datasheet')}
              title="Datasheet View"
            ><Table size={18} /></button>
          </div>
          <h1>Audit Trail - CRUD</h1>
          <span className="stui-module-header-meta">
            {loading ? '' : `${totalCount.toLocaleString()} record${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className="stui-module-header-actions">

          {/* Date filter button + popover */}
          <div style={{ position: 'relative' }} ref={dateFilterRef}>
            <button
              className="stui-crud-btn"
              onClick={() => setShowDateFilter(v => !v)}
              title={dateFilterActive ? `Filtered: ${appliedFrom || '…'} – ${appliedTo || '…'}` : 'Filter by date'}
              style={dateFilterActive ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)' } : {}}
            ><Calendar size={17} /></button>
            {showDateFilter && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200,
                background: 'var(--color-bg-white)', border: '1px solid var(--color-border)',
                borderRadius: 6, boxShadow: 'var(--shadow-md)', padding: '12px 14px',
                minWidth: 230, display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div className="stui-field-group">
                  <label>From</label>
                  <input type="date" className="stui-field-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                </div>
                <div className="stui-field-group">
                  <label>To</label>
                  <input type="date" className="stui-field-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {(dateFrom || dateTo || appliedFrom || appliedTo) && (
                    <button className="stui-btn stui-btn-secondary" onClick={clearDateFilter}>Clear</button>
                  )}
                  <button className="stui-btn stui-btn-primary" onClick={applyDateFilter}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="stui-search-wrapper">
            <span
              className={`stui-search-icon${searchTerm ? ' stui-search-icon-clearable' : ''}`}
              onMouseEnter={() => setSearchHovered(true)}
              onMouseLeave={() => setSearchHovered(false)}
              onClick={showX ? () => { setSearchTerm(''); submitSearch(''); } : undefined}
            >
              {showX ? <X size={16} /> : <Search size={16} />}
            </span>
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search changes (3+ chars, press Enter)..."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                if (!e.target.value) submitSearch('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitSearch(e.target.value);
                }
              }}
            />
          </div>

          {/* 6-dot */}
          <ActionMenu
            onDownload={() => setShowDownload(true)}
            showPurge={isSysAdmin}
            onPurge={() => setShowPurgeModal(true)}
          />
        </div>
      </div>

      {/* Content */}
      {viewMode === 'datasheet' ? (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={visibleData}
            columns={COLUMNS}
            filters={filters}
            setFilters={setFilters}
          />
          <Pagination page={page} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} rowCount={visibleData.length} loading={loading} onPage={loadData} onPageSize={ps => { setPageSize(ps); }} />
        </div>
      ) : (
        <div className="stui-list-view">
          {/* Left: list panel */}
          <div className={`stui-list-panel${listCollapsed ? ' collapsed' : ''}`}>
            <div className="stui-list-panel-header">
              {listCollapsed ? (
                <button className="stui-list-panel-expand-btn" onClick={() => setListCollapsed(false)} title="Expand panel">
                  <PanelLeftOpen size={16} />
                </button>
              ) : (
                <button className="stui-list-panel-collapse-btn" onClick={() => setListCollapsed(true)} title="Collapse panel">
                  <PanelLeftClose size={16} />
                </button>
              )}
            </div>
            <div className="stui-list-scroll" style={listCollapsed ? { display: 'none' } : {}}>
              {loading && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
                  Loading...
                </div>
              )}
              {!loading && visibleData.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
                  No records found.
                </div>
              )}
              {visibleData.map((row, i) => {
                const colors = ACTION_COLORS[row.action] || {};
                const isSelected = selectedRow === i;
                return (
                  <button
                    key={i}
                    className={`stui-list-item${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedRow(isSelected ? null : i)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                      <span className="stui-list-item-primary">
                        {row.recordIdentifier !== '—' ? row.recordIdentifier : `Record #${row.recordPk}`}
                      </span>
                      <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, flexShrink: 0, ...colors }}>
                        {row.action}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: detail panel */}
          <div className="stui-detail-panel">
            {selectedRow == null ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-text-muted)', fontSize: 13 }}>
                Select a record to view details
              </div>
            ) : (() => {
              const row = visibleData[selectedRow];
              const colors = ACTION_COLORS[row.action] || {};
              return (
                <div className="stui-detail-content">
                  <div className="stui-detail-grid">
                    <div className="stui-field-group label-left stui-col-12">
                      <label>Date</label>
                      <span className="stui-field-display">{row.changedAt}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-12">
                      <label>User</label>
                      <span className="stui-field-display">{row.changedBy}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Table</label>
                      <span className="stui-field-display">{row.tableName}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Record ID</label>
                      <span className="stui-field-display">{row.recordPk}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Identifier</label>
                      <span className="stui-field-display">{row.recordIdentifier}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Action</label>
                      <span className="stui-field-display">
                        <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600, ...colors }}>
                          {row.action}
                        </span>
                      </span>
                    </div>
                    {row.fieldName !== '—' && (
                      <div className="stui-field-group label-left stui-col-16">
                        <label>Field</label>
                        <span className="stui-field-display">{row.fieldName}</span>
                      </div>
                    )}
                    <div className="stui-field-group label-left stui-col-24">
                      <label>Old Value</label>
                      <span className="stui-field-display" style={{ wordBreak: 'break-word' }}>
                        {row.action === 'CREATE' ? 'N/A' : row.oldValue}
                      </span>
                    </div>
                    <div className="stui-field-group label-left stui-col-24">
                      <label>New Value</label>
                      <span className="stui-field-display" style={{ wordBreak: 'break-word' }}>
                        {row.action === 'DELETE' ? 'N/A' : row.newValue}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Pagination — list view only (datasheet has it inline above) */}
      {viewMode === 'list' && (
        <Pagination page={page} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} rowCount={visibleData.length} loading={loading} onPage={loadData} onPageSize={ps => setPageSize(ps)} />
      )}

      {/* Purge modal */}
      {showPurgeModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: 380 }}>
            <div className="modal-header">
              <h3>Purge Old Records</h3>
              <button onClick={() => { setShowPurgeModal(false); setPurgeDays(''); }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-md)' }}>
              <p style={{ fontSize: 13, marginBottom: 16, color: 'var(--color-text-muted)' }}>
                Permanently deletes audit records older than the specified number of days for the current database. This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 13 }}>Delete records older than</span>
                <input
                  ref={purgeDaysRef}
                  type="number" min="1"
                  className="stui-field-input"
                  style={{ width: 70 }}
                  value={purgeDays}
                  onChange={e => setPurgeDays(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePurge()}
                  placeholder="days"
                />
                <span style={{ fontSize: 13 }}>days</span>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="stui-btn stui-btn-secondary" onClick={() => { setShowPurgeModal(false); setPurgeDays(''); }}>Cancel</button>
                <button className="btn btn-danger" onClick={handlePurge} disabled={purging || !purgeDays}>
                  {purging ? 'Purging...' : 'Purge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DownloadModal
        isOpen={showDownload}
        onClose={() => setShowDownload(false)}
        data={visibleData}
        columns={COLUMNS}
        tableName="Audtrl_CRUD"
      />
    </div>
  );
};

const Pagination = ({ page, totalPages, totalCount, pageSize, rowCount, loading, onPage, onPageSize }) => (
  <div className="stui-pagination">
    <div className="stui-pagination-info">
      <span>Showing {rowCount.toLocaleString()} of {totalCount.toLocaleString()} records</span>
    </div>
    <div className="stui-pagination-controls">
      <label className="stui-pagination-pagesize">
        <span>Per page:</span>
        <select value={pageSize} onChange={e => { onPageSize(Number(e.target.value)); onPage(1); }}>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </label>
      <div className="stui-pagination-nav">
        <button className="stui-pagination-btn" onClick={() => onPage(1)} disabled={page <= 1 || loading} title="First page"><ChevronFirst size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(page - 1)} disabled={page <= 1 || loading} title="Previous page"><ChevronLeft size={18} /></button>
        <span className="stui-pagination-page-info">Page {page} of {totalPages}</span>
        <button className="stui-pagination-btn" onClick={() => onPage(page + 1)} disabled={page >= totalPages || loading} title="Next page"><ChevronRight size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(totalPages)} disabled={page >= totalPages || loading} title="Last page"><ChevronLast size={18} /></button>
      </div>
    </div>
  </div>
);

function formatUtcDate(iso) {
  if (!iso) return '—';
  try {
    const utcStr = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    return new Date(utcStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
}

export default AuditTrailCrudPage;
